// 微信 JS-SDK 工具类
import { getCurrentProjectConfig } from '@/config/projectConfig.js'

// 获取当前项目的微信配置
const getWechatConfig = () => {
  const configResult = getCurrentProjectConfig()
  return configResult.config?.wechat || {}
}

// 获取微信 appid
const getWechatAppid = () => {
  const configResult = getCurrentProjectConfig()
  return configResult.appid || ''
}
const getApiBaseUrl = () => {
  // 生产环境：使用相对路径（依赖 webpack 代理配置）
  // 开发环境：也使用相对路径，确保一致性
  return process.env.VUE_APP_BASE_API
}

// 检测是否为微信浏览器
export const isWechatBrowser = () => {
  const userAgent = navigator?.userAgent?.toLowerCase() || ''
  return /micromessenger/.test(userAgent)
}

// 检测是否为微信小程序
export const isWechatMiniProgram = () => {
  const userAgent = navigator?.userAgent?.toLowerCase() || ''
  return /miniProgram/i.test(userAgent) || /wxwork/i.test(userAgent)
}

// 微信SDK初始化状态
let wxSdkInitialized = false

// 签名缓存（减少重复请求）
const signatureCache = {
  data: null,
  url: '',
  timestamp: 0,
  maxAge: 5 * 60 * 1000 // 5分钟有效期
}

// 签名请求状态（防止重复请求）
let signatureRequestPending = false
let signatureRequestPromise = null

// 初始化微信JS-SDK
export const initWxSdk = async (config = {}) => {
  if (!isWechatBrowser()) {
    console.warn('[WechatSDK] 非微信浏览器环境，跳过微信SDK初始化')
    return false
  }

  if (wxSdkInitialized) {
    console.log('[WechatSDK] 微信SDK已初始化')
    return true
  }

  // 检查当前URL是否为开发环境IP
  const currentUrl = window?.location?.href || ''
  const isDevEnv = currentUrl.startsWith('http://192.168.') || 
                   currentUrl.startsWith('http://localhost') ||
                   currentUrl.includes(':808') || 
                   currentUrl.includes(':8080')

  try {
    console.log('[WechatSDK] 开始初始化微信SDK...')
    console.log('[WechatSDK] 当前环境:', isDevEnv ? '开发环境(IP)' : '生产环境(域名)')
    
    // 获取服务端签名
    const signatureData = await fetchWxSignature()
    
    if (!signatureData || !signatureData.success) {
      console.error('[WechatSDK] 获取签名失败:', signatureData?.message || '未知错误')
      
      // 如果是开发环境，尝试使用模拟数据
      if (isDevEnv) {
        console.warn('[WechatSDK] 开发环境签名获取失败，使用模拟数据进行测试')
        return await initWithMockData()
      }
      
      return false
    }

    console.log('[WechatSDK] 签名数据获取成功:', signatureData.data)

    // 等待微信SDK加载完成
    await loadWxSdkScript()

    // 配置微信SDK
    return await new Promise((resolve, reject) => {
      const wxConfig = {
        debug: false, // 关闭调试模式
        appId: signatureData.data.appId || getWechatAppid(), // 必填，公众号的唯一标识
        timestamp: signatureData.data.timestamp, // 必填，生成签名的时间戳
        nonceStr: signatureData.data.nonceStr, // 必填，生成签名的随机串
        signature: signatureData.data.signature, // 必填，签名
        jsApiList: [
          'updateAppMessageShareData', // 分享给朋友
          'updateTimelineShareData', // 分享到朋友圈
          'onMenuShareAppMessage', // 旧版分享给朋友（兼容）
          'onMenuShareTimeline', // 旧版分享到朋友圈（兼容）
          'onMenuShareQQ', // 分享到QQ
          'onMenuShareWeibo' // 分享到微博
        ]
      }
      
      console.log('[WechatSDK] wx.config配置:', wxConfig)
      
      window.wx.config(wxConfig)

      window.wx.ready(() => {
        wxSdkInitialized = true
        console.log('[WechatSDK] 微信SDK初始化成功')
        resolve(true)
      })

      window.wx.error((err) => {
        console.error('[WechatSDK] 微信SDK初始化失败:', err)
        
        // 特殊处理：如果是开发环境，提供更好的提示
        if (isDevEnv) {
          console.warn('[WechatSDK] 开发环境提示：wx.config失败通常是因为IP地址未配置在微信JS安全域名列表中')
          console.warn('[WechatSDK] 请使用已备案的域名访问，或在微信公众平台配置测试域名')
        }
        
        // 清除缓存，下次重新获取
        clearSignatureCache()
        reject(err)
      })
    })

  } catch (err) {
    console.error('[WechatSDK] 初始化异常:', err)
    
    // 如果是开发环境，尝试使用模拟数据
    if (isDevEnv) {
      console.warn('[WechatSDK] 初始化异常，开发环境尝试使用模拟数据')
      try {
        return await initWithMockData()
      } catch (mockErr) {
        console.error('[WechatSDK] 模拟数据初始化也失败:', mockErr)
      }
    }
    
    return false
  }
}

// 使用模拟数据初始化（仅开发环境）
const initWithMockData = async () => {
  console.log('[WechatSDK] 使用模拟数据初始化微信SDK...')
  
  try {
    // 等待微信SDK加载完成
    await loadWxSdkScript()
    
    const mockConfig = {
      debug: false,
      appId: getWechatAppid() || 'wx_mock_appid',
      timestamp: Math.floor(Date.now() / 1000).toString(),
      nonceStr: generateNonceStr(),
      signature: 'mock_signature_' + Date.now(),
      jsApiList: [
        'updateAppMessageShareData',
        'updateTimelineShareData',
        'onMenuShareAppMessage',
        'onMenuShareTimeline'
      ]
    }
    
    console.log('[WechatSDK] 使用模拟配置:', mockConfig)
    
    return await new Promise((resolve) => {
      window.wx.config(mockConfig)
      
      window.wx.ready(() => {
        wxSdkInitialized = true
        console.log('[WechatSDK] 模拟数据初始化成功（仅用于测试API调用，分享功能可能受限）')
        resolve(true)
      })
      
      window.wx.error((err) => {
        console.warn('[WechatSDK] 模拟数据wx.config失败（开发环境预期行为）:', err)
        // 开发环境即使config失败，也标记为初始化成功以便测试其他功能
        wxSdkInitialized = true
        console.log('[WechatSDK] 开发环境：标记为初始化成功，部分API可能无法正常工作')
        resolve(true)
      })
    })
  } catch (err) {
    console.error('[WechatSDK] 模拟数据初始化失败:', err)
    return false
  }
}

// 生成随机字符串
const generateNonceStr = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < 16; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

// 清除签名缓存
export const clearSignatureCache = () => {
  signatureCache.data = null
  signatureCache.url = ''
  signatureCache.timestamp = 0
  console.log('[WechatSDK] 签名缓存已清除')
}

// 重新初始化SDK（清除缓存后重新初始化）
export const reInitWxSdk = async (config = {}) => {
  console.log('[WechatSDK] 重新初始化微信SDK...')
  wxSdkInitialized = false
  clearSignatureCache()
  return await initWxSdk(config)
}

// 获取微信签名（调用后端接口）- 优化版本
const fetchWxSignature = async (retries = 3, delay = 1000) => {
  // 获取当前页面URL（去除hash部分）
  let url = window?.location?.href?.split('#')[0] || ''
  
  // 确保URL格式正确
  if (!url || !url.startsWith('http')) {
    console.error('[WechatSDK] 获取签名失败：URL无效')
    return { success: false, message: 'URL无效' }
  }
  
  console.log('[WechatSDK] 签名URL:', url)
  
  // 检查缓存
  const now = Date.now()
  if (signatureCache.data && 
      signatureCache.url === url && 
      (now - signatureCache.timestamp) < signatureCache.maxAge) {
    console.log('[WechatSDK] 使用缓存的签名数据')
    return { success: true, data: signatureCache.data }
  }
  
  // 如果已有请求在进行中，返回相同的Promise
  if (signatureRequestPending && signatureRequestPromise) {
    console.log('[WechatSDK] 等待进行中的签名请求')
    return signatureRequestPromise
  }
  
  // 标记请求开始
  signatureRequestPending = true
  signatureRequestPromise = (async () => {
    try {
      // 处理微信开发者工具环境的URL
      if (url && (url.startsWith('http://192.168.') || url.startsWith('http://localhost'))) {
        console.warn('[WechatSDK] 当前为开发环境IP地址，微信JS-SDK可能无法正常签名')
      }
      
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          const apiBaseUrl = getApiBaseUrl()
          const signUrl = apiBaseUrl + '/system/n/getJsSdkSign'
          
          console.log(`[WechatSDK] 调用签名接口 (尝试 ${attempt}/${retries}):`, signUrl)
          
          // 创建超时控制器
          const controller = new AbortController()
          const timeoutId = setTimeout(() => {
            controller.abort()
          }, 8000) // 8秒超时
          
          const response = await fetch(signUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            credentials: 'include',
            body: JSON.stringify({ key: url }),
            signal: controller.signal
          })
          
          // 清除超时定时器
          clearTimeout(timeoutId)
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          
          const result = await response.json()
          
          console.log('[WechatSDK] 签名接口响应:', result)
          
          // 处理不同的响应格式
          if (result.success === true || result.code === '000' || result.success === 'true') {
            // 数据可能在 result.data、result.result 或 result 根级别
            const data = result.data || result.result || result
            const signatureData = {
              timestamp: data?.timestamp || result.timestamp,
              nonceStr: data?.nonceStr || result.nonceStr,
              signature: data?.signature || result.signature,
              appId: data?.appId || data?.appid || result.appid || getWechatAppid()
            }
            
            // 验证签名数据完整性
            if (!signatureData.timestamp || !signatureData.nonceStr || !signatureData.signature) {
              throw new Error('签名数据不完整')
            }
            
            // 更新缓存
            signatureCache.data = signatureData
            signatureCache.url = url
            signatureCache.timestamp = now
            
            console.log('[WechatSDK] 签名数据获取成功并已缓存')
            return { success: true, data: signatureData }
          } else {
            console.error('[WechatSDK] 获取签名失败:', result.msg || result.message)
            // 如果还有重试机会，继续重试
            if (attempt < retries) {
              const waitTime = delay * Math.pow(2, attempt - 1) // 指数退避
              console.log(`[WechatSDK] 等待 ${waitTime}ms 后重试...`)
              await new Promise(resolve => setTimeout(resolve, waitTime))
              continue
            }
            return { success: false, message: result.msg || result.message || '获取签名失败' }
          }
        } catch (err) {
          console.error(`[WechatSDK] 获取签名异常 (尝试 ${attempt}/${retries}):`, err)
          
          // 如果还有重试机会，继续重试
          if (attempt < retries) {
            const waitTime = delay * Math.pow(2, attempt - 1) // 指数退避
            console.log(`[WechatSDK] 等待 ${waitTime}ms 后重试...`)
            await new Promise(resolve => setTimeout(resolve, waitTime))
            continue
          }
          
          // 如果所有重试都失败，返回模拟数据用于测试（仅开发环境）
          if (url.startsWith('http://192.168.') || url.startsWith('http://localhost')) {
            console.warn('[WechatSDK] 所有重试失败，开发环境使用模拟签名数据')
            const mockData = {
              timestamp: Math.floor(Date.now() / 1000).toString(),
              nonceStr: generateNonceStr(),
              signature: 'mock_signature_for_test_' + Date.now(),
              appId: getWechatAppid()
            }
            return { success: true, data: mockData }
          }
          
          return { success: false, message: err.message || '获取签名失败' }
        }
      }
      
      // 理论上不会到达这里
      return { success: false, message: '获取签名失败' }
    } finally {
      // 清除请求状态
      signatureRequestPending = false
      signatureRequestPromise = null
    }
  })()
  
  return signatureRequestPromise
}

// 加载微信SDK脚本
// 微信SDK脚本加载状态
let wxSdkLoading = false
let wxSdkLoadingPromise = null

// 加载微信SDK脚本（支持提前加载和动态加载两种方式）
const loadWxSdkScript = () => {
  // 如果已经加载完成，直接返回
  if (window?.wx) {
    console.log('[WechatSDK] 微信SDK已提前加载')
    return Promise.resolve()
  }
  
  // 如果正在加载中，返回同一个Promise
  if (wxSdkLoading && wxSdkLoadingPromise) {
    console.log('[WechatSDK] 等待微信SDK加载完成...')
    return wxSdkLoadingPromise
  }
  
  // 标记开始加载
  wxSdkLoading = true
  wxSdkLoadingPromise = new Promise((resolve, reject) => {
    // 设置加载超时（15秒）
    const timeoutId = setTimeout(() => {
      console.error('[WechatSDK] 微信SDK加载超时')
      wxSdkLoading = false
      wxSdkLoadingPromise = null
      reject(new Error('微信SDK加载超时'))
    }, 15000)
    
    // 检查SDK是否已经加载（可能在index.html中提前加载）
    const checkInterval = setInterval(() => {
      if (window?.wx) {
        clearInterval(checkInterval)
        clearTimeout(timeoutId)
        wxSdkLoading = false
        console.log('[WechatSDK] 微信SDK脚本加载成功')
        resolve()
      }
    }, 100)
    
    // 启动动态加载作为备用
    const script = document.createElement('script')
    script.src = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js'
    script.onload = () => {
      clearInterval(checkInterval)
      clearTimeout(timeoutId)
      wxSdkLoading = false
      console.log('[WechatSDK] 微信SDK脚本动态加载成功')
      resolve()
    }
    script.onerror = () => {
      clearInterval(checkInterval)
      // 尝试备用CDN
      console.warn('[WechatSDK] 主CDN加载失败，尝试备用CDN')
      const fallbackScript = document.createElement('script')
      fallbackScript.src = 'https://res2.wx.qq.com/open/js/jweixin-1.6.0.js'
      fallbackScript.onload = () => {
        clearTimeout(timeoutId)
        wxSdkLoading = false
        console.log('[WechatSDK] 微信SDK脚本备用CDN加载成功')
        resolve()
      }
      fallbackScript.onerror = () => {
        clearTimeout(timeoutId)
        wxSdkLoading = false
        wxSdkLoadingPromise = null
        console.error('[WechatSDK] 微信SDK脚本加载失败（主CDN和备用CDN均失败）')
        reject(new Error('微信SDK加载失败'))
      }
      document.head.appendChild(fallbackScript)
    }
    document.head.appendChild(script)
  })
  
  return wxSdkLoadingPromise
}

// 设置分享数据（完善版本兼容性）
export const setShareData = (shareData) => {
  if (!window?.wx) {
    console.warn('[WechatSDK] 微信SDK未加载，无法设置分享数据')
    return
  }

  const location = window?.location
  const data = {
    title: shareData.title || document.title,
    desc: shareData.desc || '点击查看更多精彩内容',
    link: shareData.link || (location?.href || ''),
    imgUrl: shareData.imgUrl || ((location?.origin || '') + '/favicon.ico')
  }

  // 微信版本检测
  const wxVersion = getWxVersion()
  console.log('[WechatSDK] 当前微信版本:', wxVersion)
  console.log('[WechatSDK] 设置分享数据:', data)

  // 设置所有可用的API
  const setupAPIs = () => {
    // 新版接口（微信7.0+）
    const newVersionAPIs = [
      {
        name: 'updateAppMessageShareData',
        config: {
          title: data.title,
          desc: data.desc,
          link: data.link,
          imgUrl: data.imgUrl
        },
        label: '分享给朋友'
      },
      {
        name: 'updateTimelineShareData',
        config: {
          title: data.title,
          link: data.link,
          imgUrl: data.imgUrl
        },
        label: '分享到朋友圈'
      }
    ]

    // 旧版接口（微信6.0-6.9）
    const oldVersionAPIs = [
      {
        name: 'onMenuShareAppMessage',
        config: {
          title: data.title,
          desc: data.desc,
          link: data.link,
          imgUrl: data.imgUrl
        },
        label: '旧版分享给朋友'
      },
      {
        name: 'onMenuShareTimeline',
        config: {
          title: data.title,
          link: data.link,
          imgUrl: data.imgUrl
        },
        label: '旧版分享到朋友圈'
      },
      {
        name: 'onMenuShareQQ',
        config: {
          title: data.title,
          desc: data.desc,
          link: data.link,
          imgUrl: data.imgUrl
        },
        label: '分享到QQ'
      },
      {
        name: 'onMenuShareWeibo',
        config: {
          title: data.title,
          desc: data.desc,
          link: data.link,
          imgUrl: data.imgUrl
        },
        label: '分享到微博'
      }
    ]

    // 先设置新版API（使用回调方式，确保用户点击菜单时数据最新）
    newVersionAPIs.forEach(api => {
      if (typeof window.wx[api.name] === 'function') {
        window.wx[api.name]({
          title: api.config.title,
          desc: api.config.desc,
          link: api.config.link,
          imgUrl: api.config.imgUrl,
          success: function () {
            console.log(`[WechatSDK] ${api.label}配置成功`)
          },
          fail: function (err) {
            console.warn(`[WechatSDK] ${api.label}配置失败:`, err)
          }
        })
      } else {
        console.debug(`[WechatSDK] ${api.label} API不可用，跳过`)
      }
    })

    // 再设置旧版API（兼容）
    oldVersionAPIs.forEach(api => {
      if (typeof window.wx[api.name] === 'function') {
        window.wx[api.name]({
          title: api.config.title,
          desc: api.config.desc,
          link: api.config.link,
          imgUrl: api.config.imgUrl,
          type: 'link',
          dataUrl: '',
          success: function () {
            console.log(`[WechatSDK] ${api.label}配置成功`)
          },
          cancel: function () {
            console.log(`[WechatSDK] ${api.label}用户取消`)
          },
          fail: function (err) {
            console.warn(`[WechatSDK] ${api.label}配置失败:`, err)
          }
        })
      } else {
        console.debug(`[WechatSDK] ${api.label} API不可用，跳过`)
      }
    })

    console.log('[WechatSDK] 分享数据设置完成')
  }

  try {
    /**
     * 根据微信官方文档，分享数据必须在 wx.ready() 回调中设置
     * 参考：https://developers.weixin.qq.com/doc/service/guide/h5/jssdk.html
     * 
     * wx.ready() 是一个队列机制，即使SDK已初始化，多次调用 wx.ready() 
     * 也会依次执行回调函数，确保分享数据被正确设置
     */
    console.log('[WechatSDK] 在wx.ready回调中设置分享数据（按官方文档规范）')
    window.wx.ready(function () {
      console.log('[WechatSDK] wx.ready回调触发，开始设置分享数据')
      setupAPIs()
    })
  } catch (err) {
    console.error('[WechatSDK] 设置分享数据异常:', err)
  }
}

// 获取微信版本号
export const getWxVersion = () => {
  const userAgent = window?.navigator?.userAgent || ''
  const match = userAgent.match(/MicroMessenger\/(\d+\.\d+\.\d+)/)
  return match ? match[1] : 'unknown'
}

// 分享到微信好友
export const shareToFriend = (shareData) => {
  if (!isWechatBrowser()) {
    console.warn('[WechatSDK] 非微信浏览器环境')
    return false
  }
  
  setShareData(shareData)
  // 在微信中，分享操作需要用户手动点击右上角菜单
  return true
}

// 分享到朋友圈
export const shareToTimeline = (shareData) => {
  if (!isWechatBrowser()) {
    console.warn('[WechatSDK] 非微信浏览器环境')
    return false
  }
  
  setShareData(shareData)
  // 在微信中，分享操作需要用户手动点击右上角菜单
  return true
}
